"use client";

/**
 * The contact form itself — the only client island on an otherwise
 * server-rendered page, so the section's heading and intro still arrive as
 * real HTML for crawlers.
 *
 * Note the absence of `noValidate`. Two MWA sites shipped `required` inputs on
 * a `noValidate` form, which switches the browser's own checks off and lets an
 * empty submission round-trip to the server just to come back as a raw error.
 * Native validation is the first gate; app/api/contact/route.ts is the
 * authoritative one, because this endpoint is public.
 */

import { useState, type FormEvent } from "react";

type Status = "idle" | "sending" | "ok" | "error";

const field =
  "w-full rounded-sm border border-[#2C3949] bg-[#0F1A27] px-3.5 py-3 font-sans text-[15px] text-white outline-none focus:border-transparent focus:outline focus:outline-2 focus:outline-brass";
const labelText = "mb-1.5 block text-[13px] font-medium text-[#9AA6B4]";

export function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    if (data.get("website")) {
      setStatus("ok");
      form.reset();
      return;
    }

    setStatus("sending");
    setError(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: String(data.get("firstName") || ""),
          lastName: String(data.get("lastName") || ""),
          email: String(data.get("email") || ""),
          message: String(data.get("message") || ""),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          body?.error || "Sorry, something went wrong sending your message. Please try again.",
        );
      }
      form.reset();
      setStatus("ok");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
    }
  }

  if (status === "ok") {
    return (
      <div
        role="status"
        className="mt-9 max-w-[720px] rounded-sm border border-[#2C3949] bg-[#0F1A27] px-6 py-8"
      >
        <p className="font-display text-[20px] font-semibold text-white">Message received.</p>
        <p className="mt-2 text-[16px] text-[#B7C0CC]">
          Thanks for reaching out. We&rsquo;ll be in touch.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-9 grid max-w-[720px] grid-cols-1 gap-4 sm:grid-cols-2">
      <label className="block">
        <span className={labelText}>First name</span>
        <input
          type="text"
          name="firstName"
          required
          maxLength={100}
          autoComplete="given-name"
          className={field}
        />
      </label>
      <label className="block">
        <span className={labelText}>Last name</span>
        <input
          type="text"
          name="lastName"
          required
          maxLength={100}
          autoComplete="family-name"
          className={field}
        />
      </label>
      <label className="block sm:col-span-2">
        <span className={labelText}>Email</span>
        <input
          type="email"
          name="email"
          required
          maxLength={254}
          autoComplete="email"
          className={field}
        />
      </label>
      <label className="block sm:col-span-2">
        <span className={labelText}>Message</span>
        <textarea
          name="message"
          required
          maxLength={4000}
          className={`${field} min-h-[120px] resize-y`}
        />
      </label>

      {/* Honeypot — hidden from view and from the tab order. */}
      <div aria-hidden className="hidden">
        <label>
          Leave this field empty
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      {status === "error" && error && (
        <p role="alert" className="text-[14px] font-medium text-[#FCA5A5] sm:col-span-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="justify-self-start rounded-sm bg-brass px-[26px] py-3.5 font-sans text-[15px] font-bold text-ink transition-colors hover:bg-[#cf9a39] disabled:opacity-60 sm:col-span-2"
      >
        {status === "sending" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
